import axios from "axios";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  tableCellClasses,
} from "@mui/material";
import { styled } from "@mui/styles";
import { useParams } from "react-router-dom";
import ApplicationLogoVertical from "../../../components/common/ApplicationLogoVertical";
import { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import useGlobalContext from "../../../hooks/useGlobalContext";
import localize from "../../../common/localize";
import TableComponent from "../../../components/common/TableComponent";
import { generateNoDataValues } from "../../../common/utils";
import ReportSectionTitle from "../../../components/common/ReportSectionTitle";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";

export function PrintDailyRoute() {
  const { COMPANY_ENDPOINT, DAILY_ROUTE_ENDPOINT, config } = useGlobalContext();
  const { id } = useParams();
  const [dailyRouteData, setDailyRouteData] = useState();
  const [events, setEvents] = useState();
  const [companyData, setCompanyData] = useState(undefined);
  const componentRef = useRef();

  const StyledTableCell = styled(TableCell)({
    padding: "5px",
  });

  const getCompanyData = async () => {
    const companyRequestResult = await axios.get(`${COMPANY_ENDPOINT}`, config);

    if (companyRequestResult?.data?.Ok && companyRequestResult?.data?.Data) {
      setCompanyData(companyRequestResult?.data?.Data);
      return;
    }

    setCompanyData(undefined);
  };

  const getLabelValue = (value, classNameValue) => (
    <div
      style={{
        display: "flex",
        alignContent: "center",
        justifyContent: "center",
      }}
    >
      <label className={classNameValue}>{value}</label>
    </div>
  );

  const getRouteDate = (date) => {
    if (!date || date.length === 0) {
      return "";
    }
    return `${localize.route_date}: ${date.replace("00:00:00", "").trim()}`;
  };

  const getEventsFromDailyRoute = async () => {
    const dailyRouteEventsResult = await axios.get(
      `${DAILY_ROUTE_ENDPOINT}/${id}/events`,
      config
    );

    if (
      dailyRouteEventsResult?.data?.Ok &&
      dailyRouteEventsResult?.data?.Data
    ) {
      setEvents({
        RouteEvents: [...dailyRouteEventsResult?.data?.Data],
      });
      return;
    }
  };

  const getDailyRouteInfo = async () => {
    const dailyRouteResult = await axios.get(
      `${DAILY_ROUTE_ENDPOINT}/${id}`,
      config
    );

    if (dailyRouteResult?.data?.Ok && dailyRouteResult?.data?.Data) {
      setDailyRouteData(dailyRouteResult?.data?.Data);
      return;
    }

    setDailyRouteData(undefined);
  };

  useEffect(() => {
    getCompanyData();
  }, []);

  useEffect(() => {
    if (id) getDailyRouteInfo();
  }, [id]);

  useEffect(() => {
    if (dailyRouteData) {
      getEventsFromDailyRoute();
    }
  }, [dailyRouteData]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
    :root {
        --orientation: landscape;
    }
    @media print {
      @page {
        size: A4 var(--orientation);
        margin: 0;
      }
      a[href]:after {
        content: none !important;
      }
    }`,
  });

  return (
    <div className="w-full flex items-center justify-center align-center">
      <div className="w-full flex-col" style={{ width: "80%" }}>
        <div
          className="w-full flex items-center justify-center align-center"
          ref={componentRef}
        >
          <Box
            className="w-full"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <Box
              className="w-full mt-2"
              style={{ border: "1px solid black", borderRadius: "5px" }}
            >
              <TableContainer>
                <Table
                  sx={{
                    maxWidth: "100%",
                    [`& .${tableCellClasses.root}`]: {
                      borderBottom: "none",
                    },
                  }}
                  aria-label="simple table"
                >
                  <TableBody>
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <StyledTableCell scope="row" width={120}>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <ApplicationLogoVertical
                            height={80}
                            width={80}
                            path={`${process.env.PUBLIC_URL}/img/app_report_header.png`}
                          />
                        </div>
                      </StyledTableCell>

                      <TableCell width="60%">
                        <div className="w-full flex-col">
                          {getLabelValue(
                            localize.daily_route_title.toUpperCase(),
                            "report_little_text report_centered_label report_bold_text"
                          )}
                          {getLabelValue(
                            getRouteDate(dailyRouteData?.RouteDate),
                            "report_little_text report_centered_label report_bold_text"
                          )}

                          {getLabelValue(
                            `${dailyRouteData?.Name}`,
                            "report_little_text report_centered_label report_bold_text"
                          )}
                          {dailyRouteData?.PersonalAssigned &&
                            getLabelValue(
                              `${localize.assigned_to}: ${dailyRouteData.PersonalAssigned?.SurName} ${dailyRouteData.PersonalAssigned?.LastName}`,
                              "report_little_text report_centered_label report_bold_text"
                            )}
                        </div>
                      </TableCell>

                      <TableCell width={200}>
                        {companyData && (
                          <div className="w-full flex-col items-center justify-center">
                            {companyData?.Name &&
                              getLabelValue(
                                companyData.Name,
                                "report_little_text report_centered_label report_bold_text"
                              )}
                            {companyData?.Identification &&
                              getLabelValue(
                                companyData.Identification,
                                "report_little_text report_centered_label report_bold_text"
                              )}
                            {companyData?.PhoneNumber &&
                              getLabelValue(
                                companyData.PhoneNumber,
                                "report_little_text report_centered_label report_bold_text"
                              )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              className="w-full mt-2"
              style={{ border: "1px solid black", borderRadius: "5px" }}
            >
              <TableContainer>
                <Table
                  sx={{
                    maxWidth: "100%",
                    [`& .${tableCellClasses.root}`]: {
                      borderBottom: "none",
                    },
                  }}
                  aria-label="simple table"
                >
                  <TableComponent
                    reportSize
                    whiteSpaceValue="wrap"
                    noPadding
                    columnTitles={[
                      localize.code,
                      localize.event_type,
                      localize.proceeding,
                      localize.client_name,
                      localize.phone_number,
                      localize.address,
                      localize.observations,
                    ]}
                    results={
                      events?.RouteEvents && events?.RouteEvents.length > 0
                        ? events.RouteEvents.map((item, index) => ({
                            key: `item_${index}`,
                            data: [
                              <ValueOrNoDataLabel
                                data={item.EventInfo?.Code}
                              />,
                              <ValueOrNoDataLabel
                                data={item.EventInfo?.EventType?.Name}
                              />,
                              <ValueOrNoDataLabel
                                data={item.EventInfo?.ProceedingInfo?.Name}
                              />,
                              <ValueOrNoDataLabel
                                data={item.EventInfo?.ReporterName}
                              />,
                              <ValueOrNoDataLabel
                                data={item.EventInfo?.ReporterPhoneNumber}
                              />,
                              <ValueOrNoDataLabel
                                data={`${item.EventInfo?.Address} ${item.EventInfo?.AddressComplement ?? ''}`}
                              />,
                              <ValueOrNoDataLabel data={item.Observation} />,
                            ],
                          }))
                        : [generateNoDataValues(7)]
                    }
                  />
                </Table>
              </TableContainer>
            </Box>

            <Box className="w-full">
              <ReportSectionTitle title={localize.observations} />
              <div
                className="report_section_without_top_border"
                style={{
                  width: "100%",
                  minHeight: "42px",
                  padding: "5px",
                  fontSize: "10px",
                }}
              >
                {dailyRouteData?.Observation &&
                  dailyRouteData?.Observation.length > 0 && (
                    <label className="report_little_text">
                      {dailyRouteData?.Observation}
                    </label>
                  )}
              </div>
            </Box>
          </Box>
        </div>

        <div className="flex w-full justify-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handlePrint}
            style={{
              marginTop: "20px",
            }}
          >
            {localize.generate_pdf}
          </Button>
        </div>

        <div style={{ marginTop: "20px" }} />
      </div>
    </div>
  );
}
